import { Button, Typography } from '@mui/material';
import { memo, useCallback } from 'react';
import type { MarketData } from '@/common/MarketData.ts';
import { formatNumberDisplay } from '@/utils/bigUtils';

const buttonSx = {
  mr: 1,
  color: 'text.primary',
  fontSize: '0.75rem',
  minWidth: 'auto',
  padding: '6px 8px',
};

interface TickerItemProps {
  market: MarketData;
  onSelect: (marketId: number) => void;
  // The loop copy exists only to hide the marquee seam: no tab stop for it.
  presentational?: boolean;
}

export const TickerItem = memo(({ market, onSelect, presentational = false }: TickerItemProps) => {
  const handleClick = useCallback(() => {
    if (market.id != null) onSelect(market.id);
  }, [onSelect, market.id]);

  // A market without trades reports lastPrice 0; that is unknown, not a price, and its
  // 24h change is meaningless.
  const hasTraded = market.lastPrice != null && market.lastPrice > 0;
  const isUp = market.change24hPercentage >= 0;

  return (
    <Button variant="text" size="small" onClick={handleClick} sx={buttonSx} tabIndex={presentational ? -1 : undefined}>
      {market.symbol}
      {/* Tabular digits and fixed decimals keep the item width stable across updates; a strip that
          is translated by a fraction of its own width would otherwise hop on every change. */}
      <Typography variant="body2" color="text.secondary" marginX={0.5} className="numeric">
        {hasTraded ? `$${formatNumberDisplay(market.lastPrice, { decimals: market.quoteDecimals })}` : '—'}
      </Typography>
      {hasTraded ? (
        <Typography variant="body2" color={isUp ? 'success.main' : 'error.main'} className="numeric">
          {isUp ? '+' : ''}
          {market.change24hPercentage.toFixed(2)}%
        </Typography>
      ) : null}
    </Button>
  );
});
